import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toBusinessDate } from 'config/time';

/**
 * I12-1（2026-11-03 UAT）。生年月日の入力。ブラウザの日付の部品（input type="date"）をやめ、
 * 「年・月・日」の3つの選択にする（和田様の指摘。日付の部品は年を遡るのに手間がかかる）。
 *
 * 値は API と同じ「YYYY-MM-DD」。3つとも選んだときだけ onChange に日付を渡し、途中（どれかが未選択）は
 * 空文字を渡す（保存の必須の確認は今までどおり空で判断できる）。日は、選んだ年・月の日数までにする
 * （2月30日などは選べない。月を替えて日が無くなったら日の選択を外す）。
 */
export interface BirthdayInputProps {
  /** 年の選択の id（項目名の label の htmlFor から結ぶ）。 */
  id?: string;
  /** 項目名（読み上げで「生年月日 年」のように各選択の名前にする）。 */
  label: string;
  /** 「YYYY-MM-DD」（時刻つきの値は日付の部分を使う）か空。 */
  value?: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  /** 年の選択の最初の年（既定 1900）。最後は今年。 */
  fromYear?: number;
}

type Parts = { year: string; month: string; day: string };

const EMPTY: Parts = { year: '', month: '', day: '' };

/** 「YYYY-MM-DD」を年・月・日に分ける。形が違えば空。 */
export const splitDate = (value?: string | null): Parts => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  if (!match) return EMPTY;
  return { year: match[1], month: String(Number(match[2])), day: String(Number(match[3])) };
};

/** その年・月の日数（月が無ければ31）。暦の計算だけで出す（端末の時間帯の Date を使わない。EV-116）。 */
export const daysInMonth = (year: string, month: string): number => {
  if (!month) return 31;
  const m = Number(month);
  if (m === 2) {
    // 年が未選択なら、うるう年（2月29日を選べる）として数える。
    const y = year ? Number(year) : 2000;
    const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
    return leap ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(m) ? 30 : 31;
};

/** 年・月・日がそろっていれば「YYYY-MM-DD」、そうでなければ空。 */
export const joinDate = ({ year, month, day }: Parts): string =>
  year && month && day ? `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}` : '';

const BirthdayInput: React.FC<BirthdayInputProps> = ({
  id,
  label,
  value,
  onChange,
  disabled,
  className = 'border p-2 rounded-md',
  fromYear = 1900,
}) => {
  const { t } = useTranslation('common');
  const [parts, setParts] = useState<Parts>(() => splitDate(value));

  // 親の値が外から変わった（取り消し・編集対象の切り替え）ときだけ合わせる。途中の選択（親も今の選択の結果も空）は
  // 消さない。
  useEffect(() => {
    const external = splitDate(value);
    if (joinDate(external) !== joinDate(parts)) setParts(external);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const update = (key: keyof Parts, next: string) => {
    const changed = { ...parts, [key]: next };
    if (changed.day && Number(changed.day) > daysInMonth(changed.year, changed.month)) changed.day = '';
    setParts(changed);
    onChange(joinDate(changed));
  };

  // 今年は業務の時間帯（日本時間）の今日の年（EV-116。端末の時間帯で作らない）。
  const thisYear = Number(toBusinessDate(new Date()).slice(0, 4));
  const years: number[] = [];
  for (let year = thisYear; year >= fromYear; year -= 1) years.push(year);
  const months = Array.from({ length: 12 }, (_, index) => index + 1);
  const days = Array.from({ length: daysInMonth(parts.year, parts.month) }, (_, index) => index + 1);

  const select = (
    key: keyof Parts,
    name: string,
    options: number[],
    format: (n: number) => string,
    selectId?: string
  ) => (
    <select
      id={selectId}
      aria-label={`${label} ${name}`}
      className={className}
      value={parts[key]}
      disabled={disabled}
      onChange={(e) => update(key, e.target.value)}
    >
      <option value="">{name}</option>
      {options.map((n) => (
        <option key={n} value={String(n)}>
          {format(n)}
        </option>
      ))}
    </select>
  );

  return (
    <div role="group" aria-label={label} className="flex gap-2" data-testid="birthday-input">
      {select('year', t('birthday.year'), years, (n) => t('birthday.year-option', { n }), id)}
      {select('month', t('birthday.month'), months, (n) => t('birthday.month-option', { n }))}
      {select('day', t('birthday.day'), days, (n) => t('birthday.day-option', { n }))}
    </div>
  );
};

export default BirthdayInput;
